import { useCallback } from 'react';
import clsx from 'clsx';
import { AddonAvailabilityDateRange } from '@/types/addon-management.types';
import { formatDisplayDate } from '@/utils/Date';

export const MAX_ADDON_AVAILABILITY_DATE_RANGES = 10;

interface AddonAvailabilityDateRangesProps {
  ranges: AddonAvailabilityDateRange[];
  onChange: (ranges: AddonAvailabilityDateRange[]) => void;
  disabled?: boolean;
  error?: string;
}

const toInputDate = (value?: string) => value || '';

const formatRangeLabel = (range: AddonAvailabilityDateRange) => {
  if (!range.start_date || !range.end_date) return '';
  const start = new Date(`${range.start_date}T00:00:00`);
  const end = new Date(`${range.end_date}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  return `${formatDisplayDate(start)} – ${formatDisplayDate(end)}`;
};

export const AddonAvailabilityDateRanges = ({
  ranges,
  onChange,
  disabled = false,
  error,
}: AddonAvailabilityDateRangesProps) => {
  const handleRangeChange = useCallback(
    (index: number, field: keyof AddonAvailabilityDateRange, value: string) => {
      const next = ranges.map((range, i) =>
        i === index ? { ...range, [field]: value } : range
      );
      onChange(next);
    },
    [onChange, ranges]
  );

  const handleAddRange = () => {
    if (ranges.length >= MAX_ADDON_AVAILABILITY_DATE_RANGES) return;
    onChange([...ranges, { start_date: '', end_date: '' }]);
  };

  const handleRemoveRange = (index: number) => {
    onChange(ranges.filter((_, i) => i !== index));
  };

  return (
    <div className="col-span-2">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          Availability date ranges
        </label>
        {!disabled && ranges.length < MAX_ADDON_AVAILABILITY_DATE_RANGES && (
          <button
            type="button"
            onClick={handleAddRange}
            className="btn btn-xs btn-light"
            aria-label="Add availability date range"
          >
            Add range
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Optional. Leave empty for year-round availability. You can configure up
        to {MAX_ADDON_AVAILABILITY_DATE_RANGES} non-overlapping ranges.
      </p>

      {ranges.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Always available</p>
      ) : (
        <div className="space-y-3">
          {ranges.map((range, index) => (
            <div
              key={`addon-availability-range-${index}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end border border-gray-200 rounded-lg p-3"
            >
              <div>
                <label
                  htmlFor={`availability_start_${index}`}
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Start date
                </label>
                <input
                  id={`availability_start_${index}`}
                  type="date"
                  value={toInputDate(range.start_date)}
                  onChange={(e) =>
                    handleRangeChange(index, 'start_date', e.target.value)
                  }
                  disabled={disabled}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    error && 'border-red-500'
                  )}
                />
              </div>
              <div>
                <label
                  htmlFor={`availability_end_${index}`}
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  End date
                </label>
                <input
                  id={`availability_end_${index}`}
                  type="date"
                  value={toInputDate(range.end_date)}
                  onChange={(e) =>
                    handleRangeChange(index, 'end_date', e.target.value)
                  }
                  disabled={disabled}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    error && 'border-red-500'
                  )}
                />
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveRange(index)}
                  className="btn btn-sm btn-ghost text-red-600 h-11"
                  aria-label={`Remove availability range ${index + 1}`}
                >
                  Remove
                </button>
              )}
              {disabled && formatRangeLabel(range) && (
                <p className="md:col-span-3 text-xs text-gray-500">
                  {formatRangeLabel(range)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-red-500 text-xs mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
